import { CLAIMS } from "@/constants/claims";

export type MenuItem = {
  icon: string;
  href: string | null;
  labelKey: string;
  /** Shown as-is. Skips translation when set. */
  label?: string;
  /**
   * If set, show when the user's effective claims include **any** parsed token (OR).
   * Each string may list several tokens separated by comma/semicolon/whitespace.
   * `null` = always show.
   */
  claims: string[] | null;
  /** If true, show when the user may open the admin settings hub (organization admin or project admin). */
  adminSettingsHub?: boolean;
  /** If true, show only when the user is an organization admin (not project-admin only). */
  organizationAdminOnly?: boolean;
  /** If true, mark active only when the current path exactly equals href. */
  exactMatch?: boolean;
};
export type MenuSection = { labelKey: string | null; items: MenuItem[] };

export const menuSectionsTop: readonly MenuSection[] = [
  {
    labelKey: null,
    items: [
      {
        icon: "lucide:layout-grid",
        href: "/dashboard",
        labelKey: "dashboard.dashboard",
        claims: null,
      },
    ],
  },
  {
    labelKey: "sidemenu.plananddo",
    items: [
      {
        icon: "lucide:chart-line",
        href: "/insights/summary",
        labelKey: "pagetitle.limits",
        claims: null,
      },
      { icon: "lucide:cloud-rain", href: "/weather", labelKey: "weather.weather", claims: null },
    ],
  },
  {
    labelKey: "sidemenu.operations",
    items: [
      // Marine
      { icon: "lucide:ship", href: "/marine", labelKey: "pagetitle.marine", claims: ["marine"] },
      {
        icon: "lucide:route",
        href: "/route-forecast",
        labelKey: "pagetitle.routing",
        claims: ["routing"],
      },
      {
        icon: "lucide:compass",
        href: "/inav",
        labelKey: "pagetitle.navigation",
        claims: ["navigation"],
      },
      {
        icon: "tabler:alpha",
        href: "/alphafactor",
        labelKey: "alphafactor.alphafactor",
        claims: ["alphafactor"],
      },
      {
        icon: "lucide:waves-horizontal",
        href: "/waveclimate",
        labelKey: "pagetitle.waveclimate",
        claims: ["waveclimate"],
      },
      {
        icon: "lucide:cctv",
        href: null,
        labelKey: "pagetitle.surveillance",
        claims: ["surveillance"],
      },
      {
        icon: "lucide:book-bookmark",
        href: "/logbook",
        labelKey: "pagetitle.logbook",
        claims: ["logbook"],
      },
    ],
  },

  {
    labelKey: "weather.weather",
    items: [
      {
        icon: "lucide:zap",
        href: "/lightning",
        labelKey: "pagetitle.lightning",
        claims: [CLAIMS.LIGHTNING_HISTORY, CLAIMS.LIGHTNING_LIVE, CLAIMS.LIGHTNING_NOTIFICATIONS],
      },
      {
        icon: "carbon:weather-station",
        href: null,
        labelKey: "pagetitle.observations",
        claims: [CLAIMS.OBSERVATION],
      },
      {
        icon: "lucide:map",
        href: "/weather-maps",
        labelKey: "pagetitle.weathermaps",
        claims: null,
      },
    ],
  },

  // Development
  ...(process.env.NODE_ENV === "development"
    ? [
        {
          labelKey: "sidemenu.development",
          items: [
            {
              icon: "lucide:palette",
              href: "/ui-showcase",
              labelKey: "pagetitle.ui",
              claims: null,
            },
            {
              icon: "lucide:palette",
              href: "/ui-showcase-legacy",
              labelKey: "pagetitle.ui",
              label: "Legacy UI components",
              claims: null,
            },
          ],
        },
      ]
    : []),
];

export const menuSectionsBottom: readonly MenuSection[] = [
  {
    labelKey: null,
    items: [
      {
        icon: "lucide:settings",
        href: "/locations",
        labelKey: "project.projectsettings",
        claims: null,
      },
    ],
  },
  {
    labelKey: null,
    items: [
      {
        icon: "ph:rainbow-cloud",
        href: "https://www.infoplaza.com/en/marine",
        labelKey: "weather.weatherintelligence",
        claims: null,
      },
    ],
  },
];

export const menuSectionsAuth: readonly MenuSection[] = [
  {
    labelKey: null,
    items: [
      {
        icon: "lucide:user-circle",
        href: "https://account.infoplaza.com/user/profile",
        labelKey: "usermenu.myprofilelink",
        claims: null,
      },
      {
        icon: "lucide:sliders-horizontal",
        href: "/user/settings",
        labelKey: "usermenu.settingslink",
        claims: null,
      },
    ],
  },

  {
    labelKey: "{organization.name}",
    items: [
      {
        icon: "lucide:settings",
        href: null,
        labelKey: "adminsettings.title",
        claims: null,
        adminSettingsHub: true,
      },
      {
        icon: "lucide:credit-card",
        href: "/billing",
        labelKey: "usermenu.billing",
        claims: null,
        organizationAdminOnly: true,
      },
    ],
  },
  {
    labelKey: null,
    items: [
      { icon: "lucide:log-out", href: "/logout", labelKey: "usermenu.signout", claims: null },
    ],
  },
];
